import type { PaletteId } from "@shared/events";
import { PALETTE_IDS } from "@shared/events";

/**
 * OWNED BY C1. The curated tile/theme palette (F-207, F-301). No hex input anywhere in the product;
 * users pick an id. These hexes are the source of truth and are mirrored into
 * src/client/styles/tokens.css as --c01 … --c12 (+ -ink) for both themes; palette.test.ts asserts the mirror.
 */
export interface PaletteEntry {
  id: PaletteId;
  name: string;
  light: { bg: string; ink: string };
  dark: { bg: string; ink: string };
}

export const PALETTE: Record<PaletteId, PaletteEntry> = {
  c01: { id: "c01", name: "Pine", light: { bg: "#1B6E44", ink: "#FFFFFF" }, dark: { bg: "#5FD199", ink: "#082114" } },
  c02: { id: "c02", name: "Teal", light: { bg: "#0F6E77", ink: "#FFFFFF" }, dark: { bg: "#54CBD8", ink: "#042124" } },
  c03: { id: "c03", name: "Ocean", light: { bg: "#1B55B0", ink: "#FFFFFF" }, dark: { bg: "#7FB2FF", ink: "#061A33" } },
  c04: { id: "c04", name: "Indigo", light: { bg: "#4438AE", ink: "#FFFFFF" }, dark: { bg: "#A79BFF", ink: "#120E33" } },
  c05: { id: "c05", name: "Violet", light: { bg: "#6E2E99", ink: "#FFFFFF" }, dark: { bg: "#CE9CF0", ink: "#210B2E" } },
  c06: { id: "c06", name: "Magenta", light: { bg: "#A11E67", ink: "#FFFFFF" }, dark: { bg: "#F492C4", ink: "#2E0A1D" } },
  c07: { id: "c07", name: "Crimson", light: { bg: "#AE2029", ink: "#FFFFFF" }, dark: { bg: "#FF9C9C", ink: "#300809" } },
  c08: { id: "c08", name: "Rust", light: { bg: "#A9430A", ink: "#FFFFFF" }, dark: { bg: "#FFA96B", ink: "#2C1103" } },
  c09: { id: "c09", name: "Amber", light: { bg: "#8A5E00", ink: "#FFFFFF" }, dark: { bg: "#F0C04A", ink: "#241800" } },
  c10: { id: "c10", name: "Olive", light: { bg: "#4F6A12", ink: "#FFFFFF" }, dark: { bg: "#B4D35C", ink: "#151C04" } },
  c11: { id: "c11", name: "Clay", light: { bg: "#6E4636", ink: "#FFFFFF" }, dark: { bg: "#D8A791", ink: "#1F120C" } },
  c12: { id: "c12", name: "Slate", light: { bg: "#3E4950", ink: "#FFFFFF" }, dark: { bg: "#AAB8C0", ink: "#121719" } },
  // Lighter/pastel row (D-041): soft tiles that sit well on the dark theme; dark ink keeps AA contrast in both themes.
  c13: { id: "c13", name: "Blush", light: { bg: "#E88BA5", ink: "#34101C" }, dark: { bg: "#F6C4D2", ink: "#34101C" } },
  c14: { id: "c14", name: "Peach", light: { bg: "#E89C63", ink: "#341C0A" }, dark: { bg: "#F7CFAE", ink: "#341C0A" } },
  c15: { id: "c15", name: "Butter", light: { bg: "#D8BC52", ink: "#322905" }, dark: { bg: "#F0E1A2", ink: "#322905" } },
  c16: { id: "c16", name: "Mint", light: { bg: "#6FC79C", ink: "#0B2E20" }, dark: { bg: "#B4E6CC", ink: "#0B2E20" } },
  c17: { id: "c17", name: "Sky", light: { bg: "#7DAEE6", ink: "#0C2440" }, dark: { bg: "#BBD7F5", ink: "#0C2440" } },
  c18: { id: "c18", name: "Lilac", light: { bg: "#AE93E4", ink: "#241246" }, dark: { bg: "#D4C4F3", ink: "#241246" } },
};

export const PALETTE_ORDER: readonly PaletteId[] = PALETTE_IDS;

/** CSS custom-property reference for a palette id; resolves per theme at paint time. */
export function paletteBg(id: PaletteId): string {
  return `var(--${id})`;
}
export function paletteInk(id: PaletteId): string {
  return `var(--${id}-ink)`;
}
/** Tint of a palette colour over the current surface, for soft backgrounds. */
export function paletteSoft(id: PaletteId, percent = 14): string {
  return `color-mix(in srgb, var(--${id}) ${percent}%, var(--surface))`;
}
/** Inline style bundle for a tile painted in a palette colour. */
export function paletteStyle(id: PaletteId): Record<string, string> {
  return { "--tile-bg": paletteBg(id), "--tile-ink": paletteInk(id) };
}
export function paletteName(id: PaletteId): string {
  return PALETTE[id].name;
}

